import { useState } from 'react';
import { Building2, Camera, Droplet, Egg, Leaf, Package, PartyPopper, Tractor, X } from 'lucide-react';
import { mediaUrl, useSite, type GalleryPhoto } from '../../utils/site';
import Container from '../components/Container';
import SectionHeading from '../components/SectionHeading';
import PlaceholderTile from '../components/PlaceholderTile';

const GALLERY_SECTIONS = [
  {
    title: 'The Farm & Sheds',
    tiles: [
      { icon: Building2, label: 'Add photo: Farm entrance / signage' },
      { icon: Tractor, label: 'Add photo: Cattle shed exterior' },
      { icon: Building2, label: 'Add photo: Farm grounds / layout' },
    ],
  },
  {
    title: 'Dairy Cows',
    tiles: [
      { icon: Droplet, label: 'Add photo: Herd grazing' },
      { icon: Droplet, label: 'Add photo: Milking parlour' },
      { icon: Droplet, label: 'Add photo: Calves' },
    ],
  },
  {
    title: 'Poultry',
    tiles: [
      { icon: Egg, label: 'Add photo: Poultry shed' },
      { icon: Egg, label: 'Add photo: Free-range birds' },
    ],
  },
  {
    title: 'Sheep Rearing',
    tiles: [
      { icon: Leaf, label: 'Add photo: Sheep flock' },
      { icon: Leaf, label: 'Add photo: Sheep shed' },
    ],
  },
  {
    title: 'Natural Agriculture',
    tiles: [
      { icon: Leaf, label: 'Add photo: Farm fields' },
      { icon: Leaf, label: 'Add photo: Organic inputs / composting' },
    ],
  },
  {
    title: 'Processing & Packaging',
    tiles: [
      { icon: Package, label: 'Add photo: Ghee preparation' },
      { icon: Package, label: 'Add photo: Packaging line' },
      { icon: Package, label: 'Add photo: Finished products ready for dispatch' },
    ],
  },
  {
    title: 'Farm Events',
    tiles: [
      { icon: PartyPopper, label: 'Add photo: Farm visit day' },
      { icon: PartyPopper, label: 'Add photo: Community / school visit' },
    ],
  },
];

// Photos are added, captioned, moved and removed on Administration ->
// Appearance -> Gallery. A section with no photos yet keeps its "coming
// soon" tiles; a new, empty section isn't shown.
export default function Gallery() {
  const site = useSite();
  const [open, setOpen] = useState<GalleryPhoto | null>(null);
  const hasPhotos = site.gallery.length > 0;
  return (
    <section id="gallery" className="scroll-mt-20">
      <section className="bg-gradient-to-b from-brand-green-50 to-surface py-16 sm:py-6">
        <Container>
          <SectionHeading
            eyebrow="Gallery"
            title="A look at life on the farm"
            subtitle={hasPhotos ? 'Moments from our farm — the herd, the flock, the fields and the people.' : "Photos from our farm are coming soon — here's what you'll see once our photo shoot is complete."}
            center
          />
        </Container>
      </section>

      <section className="py-16 sm:py-20">
        <Container className="space-y-14">
          {site.gallery_sections.map((title) => {
            const photos = site.gallery.filter((g) => g.section === title);
            const tiles = GALLERY_SECTIONS.find((s) => s.title === title)?.tiles ?? [];
            if (photos.length === 0 && tiles.length === 0) return null;
            return (
              <div key={title}>
                <h2 className="mb-5 flex items-center gap-2 text-xl font-bold text-brand-green-900">
                  <Camera className="h-5 w-5 text-brand-gold-600" />
                  {title}
                </h2>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {photos.length > 0
                    ? photos.map((p) => (
                      <button key={p.photo} type="button" onClick={() => setOpen(p)} className="group overflow-hidden rounded-xl border border-brand-green-100 bg-surface text-left shadow-card">
                        <div className="aspect-[4/3] w-full overflow-hidden">
                          <img src={mediaUrl(p.photo)} alt={p.caption || title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                        </div>
                        {p.caption && <p className="px-4 py-3 text-sm font-medium text-ink-700">{p.caption}</p>}
                      </button>
                    ))
                    : tiles.map((tile) => <PlaceholderTile key={tile.label} icon={tile.icon} label={tile.label} />)}
                </div>
              </div>
            );
          })}
        </Container>
      </section>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/85 p-4" onClick={() => setOpen(null)} role="dialog" aria-label={open.caption || 'Photo'}>
          <button type="button" onClick={() => setOpen(null)} aria-label="Close" className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white hover:bg-white/25">
            <X className="h-6 w-6" />
          </button>
          <figure className="max-h-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img src={mediaUrl(open.photo)} alt={open.caption} className="max-h-[80vh] w-auto rounded-lg object-contain" />
            {open.caption && <figcaption className="mt-3 text-center text-sm text-white">{open.caption}</figcaption>}
          </figure>
        </div>
      )}
    </section>
  );
}
